import { useRef, useEffect, useState } from 'react';

export default function CTASection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative py-28 overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-top"
        style={{
          backgroundImage: "url('https://readdy.ai/api/search-image?query=vibrant%20Indian%20restaurant%20interior%20warm%20amber%20candlelight%20evening%20dining%20tables%20elegant%20atmospheric%20golden%20orange%20tones%20artistic%20mural%20wall%20hand%20painted%20folk%20art%20festive%20cultural%20ambiance%20premium%20photography&width=1920&height=600&seq=1701&orientation=landscape')",
        }}
      ></div>

      {/* Heavy dark overlay — guarantees full text legibility */}
      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(12, 4, 0, 0.91)' }}></div>
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, rgba(80,28,0,0.45) 0%, rgba(0,0,0,0.2) 100%)' }}
      ></div>

      {/* Mural border top */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-saffron via-turmeric via-coral via-teal to-magenta"></div>

      {/* Subtle jaali dot pattern */}
      <div
        className="absolute inset-0 opacity-6 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,182,39,0.7) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      ></div>

      {/* Decorative rings */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-6 left-10 w-44 h-44 rounded-full border border-saffron/20"></div>
        <div className="absolute top-14 left-18 w-22 h-22 rounded-full border border-saffron/12"></div>
        <div className="absolute bottom-6 right-10 w-60 h-60 rounded-full border border-coral/20"></div>
        <div className="absolute bottom-16 right-22 w-32 h-32 rounded-full border border-turmeric/15"></div>
        <div className="absolute top-1/2 right-36 w-5 h-5 rounded-full bg-saffron/35"></div>
        <div className="absolute top-1/3 left-1/4 w-3 h-3 rounded-full bg-coral/35"></div>
        <div className="absolute bottom-1/3 left-1/3 w-2.5 h-2.5 rounded-full bg-turmeric/45"></div>
      </div>

      <div
        className={`relative z-10 max-w-4xl mx-auto px-6 text-center transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Ornament divider */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-saffron/70"></div>
          <i className="ri-restaurant-fill text-saffron text-xl"></i>
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-saffron/70"></div>
        </div>

        {/* Eyebrow label — fully opaque saffron */}
        <p className="font-sans text-xs uppercase tracking-[0.35em] text-saffron mb-4">
          Join Us Today
        </p>

        {/* Main heading — full white */}
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-5 leading-tight">
          Ready for a<br />
          <span className="text-saffron italic">Flavor Adventure?</span>
        </h2>

        {/* Body text — near-full white */}
        <p className="font-sans text-base sm:text-lg text-white/90 mb-8 max-w-xl mx-auto">
          To reserve your table at Chaska Indian Kitchen, simply give us a call and we&apos;ll make it happen. Let us take you on a journey through the vibrant, aromatic soul of India.
        </p>

        {/* Phone card */}
        <div className="inline-flex items-center gap-4 bg-white/12 backdrop-blur-sm border border-white/25 rounded-2xl px-6 py-4 mb-10">
          <div className="w-11 h-11 flex items-center justify-center rounded-full bg-saffron flex-shrink-0">
            <i className="ri-phone-fill text-white text-lg"></i>
          </div>
          <div className="text-left">
            <p className="font-sans text-xs text-white/75 uppercase tracking-widest mb-0.5">Call to Reserve</p>
            <a
              href="tel:+18645550123"
              className="font-serif text-2xl text-saffron font-bold cursor-pointer hover:text-turmeric transition-colors"
            >
              (864) 555-0123
            </a>
          </div>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="tel:+18645550123"
            className="bg-saffron text-white font-sans font-bold text-sm px-10 py-4 rounded-full hover:bg-coral transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 hover:scale-105"
            style={{ boxShadow: '0 4px 24px rgba(255,107,53,0.55)' }}
          >
            <i className="ri-phone-fill"></i>
            Call &amp; Reserve Now
          </a>
          <a
            href="mailto:inquiry@chaskaindiankitchen.com"
            className="bg-white/18 border border-white/35 text-white font-sans font-semibold text-sm px-10 py-4 rounded-full hover:bg-white/28 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-mail-line text-turmeric"></i>
            Email Us
          </a>
        </div>

        {/* Location + Hours — clearly readable */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-white/80">
          <div className="flex items-center gap-2">
            <i className="ri-map-pin-2-line text-saffron"></i>
            <span className="font-sans text-sm">1675 E Main St Suite B, Duncan, SC 29334</span>
          </div>
          <div className="hidden sm:block w-1 h-1 rounded-full bg-white/40"></div>
          <div className="flex items-center gap-2">
            <i className="ri-time-line text-saffron"></i>
            <span className="font-sans text-sm">Mon–Thu: 11am–9pm &bull; Fri–Sat: 11am–10pm</span>
          </div>
        </div>
      </div>

      {/* Bottom mural border */}
      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-teal via-coral via-turmeric to-saffron"></div>
    </section>
  );
}
